import { I18n } from '@app/i18n/strings-keys';
import { find, get, map } from 'lodash';

import { BehaviorSubject } from '../../rx/rx-wrapper';
import { IBaseEntityType } from '../../types/entities/base/base.type';
import { IEntityConfig } from '../../types/entities/entity-config.type';
import { HAWKER_SESSION_STATUS_VALUE } from '../../types/entities/hawker-session/hawker-session-status.enum';
import { ENTITY_TYPES } from '../../types/enums/entityTypes.enum';
import {
  formatEmployeeName,
  getColumnTipConfig,
  sortHawkerSessions,
} from './hawker-session.utils';
import { FieldList, IXVIListColumn, WIDGET_COMPONENT } from '@app/shared/types';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import {
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community/dist/lib/entities/colDef';
import { XVII18nService } from '@app/i18n/services';
import { NumberFormatService } from '../number-format/number-format.service';
import { HAWKER_SESSION_FORM_FIELD_PATH } from './hawker-session.constant';
import { WIDGET_FAMILY } from '@config/global-constants';
import { decimalNumber } from '@app/shared/common/decimal-number/decimal-number';

export const hawkerSessionEntityType: ENTITY_TYPES =
  ENTITY_TYPES.HAWKER_SESSION;
export const hawkerServiceConfig: IEntityConfig = {
  ENTITY_TYPE: hawkerSessionEntityType,
  isMasterListAvailable: true,
  isFormServiceSupported: true,
  expandSidebar: false,
  showSidebarCollapsedBtn: true,
  entityNameToCheckPermissions: ENTITY_TYPES.HAWKER_SESSION,
  buttons: {},
  apiEndpoint: hawkerSessionEntityType,
  uiRoute: hawkerSessionEntityType,
  list: {
    titleObs: new BehaviorSubject<string>(null),
    documentQuery: {
      notNeedActive: true,
      select: [
        'employee.last_name',
        'employee.first_name',
        'commission_rate.entity_id',
        'i18n',
        'created_at',
      ],
      orderby: 'created_at desc',
    },
    dependentEntities: [
      {
        entityType: ENTITY_TYPES.COMMISSION_RATE,
        query: {
          select: ['name', 'code', 'description', 'id', 'i18n'],
        },
      },
    ],
    userGridOptions: {
      enableServerSideFilter: false,
      enableServerSideSorting: false,
      suppressRowClickSelection: true,
    },
    isHiddenRefreshButton: true,
    gridOptions: {
      valueField: '_id',
      showViewSelector: false,
      showSearch: true,
      showPagination: false,
    },
    listItemsLabel: I18n.Forms.HawkerSessionForm.SessionsBrackets,
    afterFetchListData: (items, options) => {
      const dependentData = options?.dependentEntitiesData;

      const commissionRatesData: IBaseEntityType[] = get(
        dependentData,
        [ENTITY_TYPES.COMMISSION_RATE, 'items'],
        [],
      );

      const mappedItems = map(items, (item) => {
        const lastName = get(item, 'employee.last_name', '');
        const firstName = get(item, 'employee.first_name', '');
        const hawkerName = formatEmployeeName({
          Lastname: lastName,
          Firstname: firstName,
        });

        const isCanceled =
          get(item, 'status') === HAWKER_SESSION_STATUS_VALUE.Canceled;
        const commissionRateEntityId = get(item, 'commission_rate.entity_id');
        const commissionRateName = isCanceled
          ? get(item, 'commission_rate.name', '')
          : get(
              find(commissionRatesData, { entity_id: commissionRateEntityId }),
              'name',
              '',
            );

        return {
          ...item,
          hawker_name: hawkerName,
          commission_rate: {
            ...get(item, 'commission_rate', {}),
            name: commissionRateName,
          },
        };
      });

      return sortHawkerSessions(mappedItems);
    },
  },
};

const buildInformationColumn = (
  service: {
    i18nService: Pick<XVII18nService, 'instant'>;
    numberFormatService: Pick<NumberFormatService, 'getNumberFormatter'>;
  },
  options: {
    title: string;
    path: string;
    hint: string;
    valueGetter?: ((params: ValueGetterParams) => any) | string;
  },
): IXVIListColumn => {
  return {
    title: options.title,
    path: options.path,
    valueGetter: get(options, 'valueGetter'),
    defaultView: true,
    ...COLUMN_ALIGN_RIGHT,
    headerComponentParams: getColumnTipConfig(
      service.i18nService.instant(options.hint),
    ),
    valueFormatter: (params: ValueFormatterParams) => {
      return service.numberFormatService.getNumberFormatter(true)(params);
    },
  };
};

export const buildInformationColumnConfig = (
  service: {
    i18nService: Pick<XVII18nService, 'instant'>;
    numberFormatService: Pick<NumberFormatService, 'getNumberFormatter'>;
  },
  shouldOmit: boolean = false,
): FieldList => {
  const columns: IXVIListColumn[] = [
    buildInformationColumn(service, {
      title: I18n.Forms.StandWorksheetForm.TerminalSales,
      path: 'terminal_sales',
      hint: I18n.Forms.HawkerSessionForm.TerminalSalesTotalTip,
    }),
    buildInformationColumn(service, {
      title: I18n.Forms.StandWorksheetForm.Taxes,
      path: 'taxes',
      hint: I18n.Forms.HawkerSessionForm.TaxesTotalTip,
    }),
    buildInformationColumn(service, {
      title: I18n.Forms.StandWorksheetForm.Tips,
      path: 'tips',
      hint: I18n.Forms.StandWorksheetFormTips.TipsTip,
    }),
    buildInformationColumn(service, {
      title: I18n.Forms.StandWorksheetForm.CreditGiftCards,
      path: 'credit_gift_cards',
      hint: I18n.Forms.StandWorksheetFormTips.CreditGiftCardsTip,
      valueGetter: (params) => {
        const payments = get(params.data, 'payments', {}) ?? {};

        return decimalNumber(get(payments, 'credit', 0))
          .add(get(payments, 'debit', 0))
          .add(get(payments, 'gift', 0))
          .valueOf();
      },
    }),
    buildInformationColumn(service, {
      title: I18n.Forms.StandWorksheetForm.TerminalCash,
      path: 'payments.cash',
      hint: I18n.Forms.StandWorksheetFormTips.TerminalCashTip,
    }),
    buildInformationColumn(service, {
      title: I18n.Forms.StandWorksheetForm.CustomTenders,
      path: 'payments.custom',
      hint: I18n.Forms.StandWorksheetFormTips.CustomTendersTip,
    }),
    buildInformationColumn(service, {
      title: I18n.Forms.StandWorksheetForm.FeesOther,
      path: 'miscellaneous',
      hint: I18n.Forms.StandWorksheetFormTips.FeesOtherTip,
    }),
  ];

  return {
    [HAWKER_SESSION_FORM_FIELD_PATH.virtualInformationData]: {
      title: null,
      class: 'no-titled-grid no-container-border-grid',
      widget: WIDGET_COMPONENT.edit_grid_custom,
      widgetFamily: WIDGET_FAMILY.XUX,
      layout: {
        columnCount: 1,
      },
      omit: shouldOmit
        ? ({ status }: { status: HAWKER_SESSION_STATUS_VALUE }): boolean =>
            status !== HAWKER_SESSION_STATUS_VALUE.Closed
        : undefined,
      widgetData: {
        showLoader: false,
        showSearch: false,
        canAdd: false,
        canEdit: false,
        orderControls: false,
        canRemove: false,
        hideActionPanel: true,
        disableColumnSelector: true,
        multiselect: false,
        gridHeight: '95px',
        userGridOptions: {
          suppressHorizontalScroll: false,
          suppressClickEdit: true,
          suppressRowClickSelection: true,
        },
        columns,
      },
    },
  };
};
